import { useState, useEffect, useCallback } from "react";
import { setService } from "services";
import { ISetModulocker } from "config/interfaces";
import { useSession } from "context/SessionContext";
import { convertChartDetailsDataToArray, getStatusMCM } from "utils";
import { useWebSocket } from "hooks";
import { useError } from "hooks";

interface SetShort {
  IDMCM: number;
  DSMCM: string;
}
interface SelectOptions {
  label: string;
  value: string | number;
}

// const initialData = Object.freeze(initialState);

export default function useSet(installationId?: string) {
  const { setIDMCM, IDMCM } = useSession();
  const { verifyError } = useError();
  const [loading, setLoading] = useState(false);
  const [setStatus, setSetStatus] = useState({} as ISetModulocker.ControlPanel);
  const [sets, setSets] = useState([] as ISetModulocker.Set[]);
  const [MCM, setMCM] = useState({} as ISetModulocker.Set);
  const [setsShort, setSetsShort] = useState([] as SetShort[]);
  const [setModulocker, setSetModulocker] = useState(
    {} as ISetModulocker.SetInformation
  );

  const [complements, setComplements] = useState(
    [] as ISetModulocker.Complements[]
  );
  const [charts, setCharts] = useState(
    {} as { withdrawals: any; deliveries: any }
  );
  const [setSelectOptions, setSetSelectOptions] = useState(
    [] as Array<SelectOptions>
  );
  const [autoCsets, setAutoCsets] = useState([] as any);
  const [loadingSets, setLoadingSets] = useState(false);
  const [userGroups, setUserGroups] = useState(
    [] as Array<ISetModulocker.UserGroup>
  );
  const [errorMessage, setErrorMessage] = useState("");

  const { newMessage: newTransaction } = useWebSocket({
    subscribe: "/topic/frontend.transaction.all",
  });

  useEffect(() => {
    if (installationId) {
      (async () => {
        setLoadingSets(true);
        try {
          const { data } = await setService.getAllShort(installationId);
          setSetsShort(data);

          const selectedSet = IDMCM ? IDMCM : data[0].IDMCM.toString();
          if (!IDMCM) {
            setIDMCM(selectedSet);
          }

          const { data: set } = await setService.boxesStatus(selectedSet);
          setSetModulocker(set);

          let { data: dataCharts } = await setService.getCharts(selectedSet);

          const formatedDataCharts = formatDetailsToChart(dataCharts);
          setCharts(formatedDataCharts);
        } catch (error: any) {
          verifyError(error.response.status);
          setErrorMessage(error?.response?.data?.message);
        } finally {
          setLoadingSets(false);
        }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    (async () => {
      if (newTransaction) {
        try {
          const { data: set } = await setService.boxesStatus(IDMCM);
          setSetModulocker(set);

          let { data: dataCharts } = await setService.getCharts(IDMCM);

          const formatedDataCharts = formatDetailsToChart(dataCharts);
          setCharts(formatedDataCharts);
        } catch (error: any) {
          verifyError(error.response.status);
          setErrorMessage(error?.response?.data?.message);
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newTransaction]);

  async function getAll(props?: { withLoading: boolean }) {
    if (props?.withLoading) setLoading(true);

    try {
      const { data } = await setService.getAll();

      const newData = data.map((set) => {
        return {
          ...set,
          ENDERECO: `${set.LOGRADOURO}, ${set.NUMERO}`,
          STATUS: getStatusMCM(set.STMCM),
        };
      });

      setSets(newData);
    } catch (error: any) {
      verifyError(error.response.status);
      setErrorMessage(error?.response?.data?.message);
    } finally {
      if (props?.withLoading) setLoading(false);
    }
  }

  async function getOne({
    IDMCM,
    withLoading,
  }: {
    IDMCM: string;
    withLoading?: boolean;
  }) {
    if (withLoading) setLoading(true);

    try {
      const { data } = await setService.getOne(IDMCM);
      setMCM(data[0]);
    } catch (error: any) {
      // verifyError(error.response.status);
      setErrorMessage(error?.response?.data?.message);
    } finally {
      if (withLoading) setLoading(false);
    }
  }

  async function getOneStatus(IDMCM: string) {
    setLoading(true);
    try {
      const { data } = await setService.getControlPanelOne(IDMCM);

      setSetStatus(data);
    } catch (error: any) {
      verifyError(error.response.status);
      setSetStatus({} as ISetModulocker.ControlPanel);
      setErrorMessage(error?.response?.data?.message);
    } finally {
      setLoading(false);
    }
  }

  async function sendSirenCommand(IDMCM: string) {
    setLoading(true);
    try {
      await setService.sendTurnOffSirene(IDMCM);

      const { data } = await setService.getControlPanelOne(IDMCM);
      setSetStatus(data);
    } catch (error: any) {
      verifyError(error.response.status);
      setSetStatus({} as ISetModulocker.ControlPanel);
      setErrorMessage(
        "Erro ao enviar a requisição para a sirene:" + error.message
      );
    } finally {
      setLoading(false);
    }
  }

  async function getAllSetsShort(props?: {
    IDSITE?: string;
    withLoading?: boolean;
  }) {
    if (props?.withLoading) setLoading(true);

    try {
      if (props?.IDSITE) {
        const { data } = await setService.getAllShort(props.IDSITE);
        const mySets = data.map((set) => {
          return {
            label: set.DSMCM,
            value: set.IDMCM,
          };
        });
        setSetsShort(data);
        const autoCset = data.map((set: any) => {
          return {
            label: set.DSMCM,
            value: set.IDMCM,
          };
        });
        setAutoCsets(autoCset);
        return setSetSelectOptions(mySets);
      } else {
        setSetsShort([] as SetShort[]);
        setSetSelectOptions([] as SelectOptions[]);
      }
    } catch (error: any) {
      verifyError(error.response.status);
      setErrorMessage(error?.response?.data?.message);
    } finally {
      if (props?.withLoading) setLoading(false);
    }
  }

  const formatDetailsToChart = useCallback((dataCharts: any) => {
    const deliveriesDetailsData = dataCharts.deliveries.details.map(
      (detail: any) => {
        const detailData = convertChartDetailsDataToArray(detail.data);
        return { ...detail, data: detailData };
      }
    );

    const withdrawalsDetailsData = dataCharts.withdrawals.details.map(
      (detail: any) => {
        const detailData = convertChartDetailsDataToArray(detail.data);
        return { ...detail, data: detailData };
      }
    );

    dataCharts.deliveries.details = deliveriesDetailsData;
    dataCharts.withdrawals.details = withdrawalsDetailsData;

    return dataCharts;
  }, []);

  const getSetInformations = async (setId: string) => {
    setLoadingSets(true);

    try {
      let { data: dataCharts } = await setService.getCharts(setId);

      const formatedDataCharts = formatDetailsToChart(dataCharts);
      setCharts(formatedDataCharts);
    } catch (error: any) {
      verifyError(error.response.status);
      setCharts({} as { withdrawals: any; deliveries: any });
      // setErrorMessage(error?.response?.data?.message);
    }

    try {
      // const { data: dataCompl, status: statusCompl } =
      const { data: dataCompl } = await setService.getComplements(setId);
      setComplements(dataCompl);
    } catch (error: any) {
      verifyError(error.response.status);
      setComplements([] as ISetModulocker.Complements[]);
      // setErrorMessage(error?.response?.data?.message);
    }

    try {
      const { data } = await setService.boxesStatus(setId);

      setSetModulocker(data);
    } catch (error: any) {
      verifyError(error.response.status);
      setSetModulocker({} as ISetModulocker.SetInformation);
      // setErrorMessage(error?.response?.data?.message);
    }
    setLoadingSets(false);
  };

  async function getComplementsBySet(IDSITE: string, setId: string) {
    const { data } = await setService.getAllShort(IDSITE);
    const selectedSet = data.find((set) => set.DSMCM === setId);

    setLoading(true);
    try {
      const { data, status } = await setService.getComplements(
        String(selectedSet?.IDMCM)
      );
      if (status === 204)
        return setComplements([] as ISetModulocker.Complements[]);
      return setComplements(data);
    } catch (error: any) {
      verifyError(error.response.status);
      setErrorMessage(error?.response?.data?.message);
    } finally {
      setLoading(false);
    }
  }

  async function getUserGroupsBySet(IDSITE: string, setId: string) {
    const { data: sets } = await setService.getAllShort(IDSITE);
    const selectedSet = sets.find((set) => set.DSMCM === setId);

    setLoading(true);
    try {
      const { data } = await setService.getListUserGroupsBySet(
        selectedSet?.IDMCM!
      );
      return setUserGroups(data);
    } catch (error: any) {
      verifyError(error.response.status);
      setErrorMessage(error?.response?.data?.message);
    } finally {
      setLoading(false);
    }
  }

  return {
    loading,
    loadingSets,
    setStatus,
    sets,
    setsShort,
    setModulocker,
    errorMessage,
    getAll,
    getOneStatus,
    getOne,
    MCM,
    getSetInformations,
    charts,
    getComplementsBySet,
    complements,
    getAllSetsShort,
    setSelectOptions,
    getUserGroupsBySet,
    userGroups,
    sendSirenCommand,
    autoCsets,
  };
}
